import { z } from "zod";
import { parisToday } from "./dates";
import { MAX_AMOUNT_CENTS } from "./money";
import { withinMaxTotal } from "./sales";
import { addressSchema, ATTACHMENT_MAX_BYTES, attachmentSchema } from "./schemas";
import { serialSchema, serialsSchema } from "./stock";
import { uniqueBy } from "./unique";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, "errors.field.tooLong")
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();
const isoDate = z.iso.date("errors.field.date");
const quantity = z.coerce.number().int("errors.field.number").min(0, "errors.field.positive").max(100_000);

export const CARRIER_VALUES = ["colissimo", "chronopost", "other"] as const;
/** Service of a parcel the company handed over itself (« Livré moi-même »): no carrier, no label. */
export const HAND_DELIVERY_SERVICE = "hand_delivery";
export const COLISSIMO_SERVICES = ["DOM", "DOS", "COLD"] as const;

const parcelCm = z.coerce.number().int("errors.field.number").min(1, "errors.field.positive").max(200, "errors.field.number");

/** An envelope (« pli ») is flat: it has no height. */
const heightCm = parcelCm.nullable().optional();
const heightUnlessEnvelope = (p: { envelope?: boolean; heightCm?: number | null }) => p.envelope === true || p.heightCm != null;

/** One parcel of a shipment: a stocked box (or envelope) or free dimensions, and its weight. */
export const shipmentParcelSchema = z
  .object({
    boxId: z.string().max(40).nullable().optional(),
    /** A flat envelope (« pli »): length and width only. */
    envelope: z.boolean().optional(),
    lengthCm: parcelCm,
    widthCm: parcelCm,
    heightCm,
    weightGrams: z.coerce.number().int().min(1, "errors.field.positive").max(30_000),
  })
  .refine(heightUnlessEnvelope, { path: ["heightCm"], message: "errors.field.required" });
export type ShipmentParcel = z.output<typeof shipmentParcelSchema> & { trackingNumber?: string | null };

/** One step for the warehouse: pick quantities, label (or tracking), stock out, customer notified. */
export const shipmentSchema = z.object({
  orderId: z.uuid(),
  lines: z
    .array(z.object({ orderLineId: z.uuid(), quantity, serials: serialsSchema }))
    .min(1, "errors.field.required")
    .refine(...uniqueBy("orderLineId")),
  carrier: z.enum(CARRIER_VALUES),
  service: z.enum(COLISSIMO_SERVICES).default("DOM"),
  /** Total weight (the sum of the parcels when they are given). */
  weightGrams: z.coerce.number().int().min(1, "errors.field.positive").max(300_000),
  /** The boxes of the shipment; one parcel when left out. */
  parcels: z.array(shipmentParcelSchema).min(1).max(20).optional(),
  trackingNumber: optionalText(64),
  generateLabel: z.boolean().default(true),
  /** Confirmed by the user: units set aside for other orders may be taken (they are released from them). */
  takeReserved: z.boolean().default(false),
});
export type ShipmentInput = z.input<typeof shipmentSchema>;
export type ShipmentData = z.output<typeof shipmentSchema>;

export const trackingSchema = z.object({ trackingNumber: z.string().trim().min(3, "errors.field.required").max(64) });

export const SHIPMENT_KIND_VALUES = ["outbound", "return", "replacement", "free", "resend"] as const;
export const SHIPMENT_STATUS_VALUES = ["draft", "ready", "shipped", "delivered", "cancelled"] as const;

/** The parcels list: search (parcel, tracking, recipient, order) and filters, page by page. */
export const SHIPMENT_SORT_KEYS = ["date", "number", "recipient", "carrier", "tracking", "status"] as const;

export const shipmentQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  kind: z.enum(SHIPMENT_KIND_VALUES).optional(),
  status: z.enum(SHIPMENT_STATUS_VALUES).optional(),
  carrier: z.enum(CARRIER_VALUES).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Sortable columns of the list; the newest first by default. */
  sort: z.enum(SHIPMENT_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).default("desc"),
});

const parcel = {
  carrier: z.enum(CARRIER_VALUES).default("colissimo"),
  service: z.enum(COLISSIMO_SERVICES).default("DOM"),
  weightGrams: z.coerce.number().int().min(1, "errors.field.positive").max(30_000),
  trackingNumber: optionalText(64),
  generateLabel: z.boolean().default(false),
};

/** Stock leaving without a sales order (samples, loans, internal use): delivery note, label and stock out. */
export const freeShipmentSchema = z.object({
  ...parcel,
  recipientName: z.string().trim().min(1, "errors.field.required").max(200, "errors.field.tooLong"),
  email: optionalText(200),
  phone: optionalText(40),
  address: addressSchema,
  note: optionalText(2000),
  lines: z
    .array(
      z.object({
        variantId: z.uuid("errors.field.required"),
        quantity: z.coerce.number().int().min(1, "errors.field.positive").max(100_000),
        serials: serialsSchema,
      }),
    )
    .min(1, "errors.field.required")
    .max(200),
});
export type FreeShipmentInput = z.input<typeof freeShipmentSchema>;
export type FreeShipmentData = z.output<typeof freeShipmentSchema>;

/** Sends the same parcel again (lost or damaged in transit): new label, goods leave stock again. */
export const resendSchema = z.object({
  ...parcel,
  note: optionalText(2000),
  /** Tracked articles: the units leaving this time, by original line. */
  lines: z
    .array(z.object({ lineId: z.uuid(), serials: z.array(serialSchema).max(10_000) }))
    .refine(...uniqueBy("lineId"))
    .optional(),
});
export type ResendInput = z.input<typeof resendSchema>;

// ── Purchasing ───────────────────────────────────────────────────────────────

/**
 * An order is prepared, sent, received (at once or bit by bit), then its reception is closed: what
 * did not come is no longer expected and the order waits for the supplier's invoice
 * (« invoice_pending »). The invoice deposited, the order is closed (« closed »), i.e. archived.
 */
export const PO_STATUS_VALUES = [
  "draft",
  "sent",
  "confirmed",
  "partially_received",
  "received",
  "invoice_pending",
  "closed",
  "cancelled",
] as const;
/** Still being worked on: shown by default in the list (archived = closed or cancelled). */
export const PO_OPEN_STATUSES = ["draft", "sent", "confirmed", "partially_received", "received", "invoice_pending"] as const;
/** Lines and header can be changed until the reception is closed. */
export const PO_EDITABLE_STATUSES = ["draft", "sent", "confirmed", "partially_received", "received"] as const;

/**
 * A place purchases are delivered to (the warehouse, an associate's address…), distinct from the
 * company's legal address (billing). Kept in Settings › Identity, chosen on each purchase order.
 */
export const deliveryAddressSchema = z.object({
  id: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{1,40}$/, "errors.field.required"),
  name: z.string().trim().min(1, "errors.field.required").max(80, "errors.field.tooLong"),
  address: addressSchema,
  /** First and last name of the person receiving the goods. */
  contactName: optionalText(120),
  phone: optionalText(40),
  email: z
    .union([z.literal(""), z.email("errors.field.email")])
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional(),
  isDefault: z.boolean().default(false),
});
export type DeliveryAddress = z.output<typeof deliveryAddressSchema>;
/** The delivery address frozen on a purchase order when it is saved. */
export type PurchaseDeliveryAddress = Omit<DeliveryAddress, "isDefault">;

export const deliveryAddressesSchema = z.object({
  addresses: z
    .array(deliveryAddressSchema)
    .max(20)
    .refine((list) => new Set(list.map((a) => a.id)).size === list.length, "errors.field.duplicate")
    .refine((list) => list.filter((a) => a.isDefault).length <= 1, "errors.field.oneDefault"),
});
export type DeliveryAddressesInput = z.input<typeof deliveryAddressesSchema>;

export const purchaseOrderSchema = z.object({
  supplierId: z.uuid("errors.field.required"),
  /**
   * Id of a delivery address of the settings. Left out: kept as it is (new order: the default one);
   * null: the company's own address.
   */
  deliveryAddressId: z.string().max(40).nullable().optional(),
  currency: z.string().length(3).toUpperCase(),
  exchangeRate: z.coerce.number().positive("errors.field.positive").max(10_000),
  orderedAt: isoDate.nullable().optional(),
  expectedAt: isoDate.nullable().optional(),
  supplierReference: optionalText(120),
  notes: optionalText(5000),
  lines: z
    .array(
      z.object({
        variantId: z.uuid(),
        supplierSku: optionalText(64),
        quantity: z.coerce.number().int().min(1, "errors.field.positive").max(1_000_000),
        unitCostCents: z.coerce
          .number()
          .int("errors.field.number")
          .min(0, "errors.field.positive")
          .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
      }),
    )
    .min(1, "errors.field.required")
    .max(500)
    .refine((lines) => withinMaxTotal(lines.map((l) => l.quantity * l.unitCostCents)), "errors.field.amountTooLarge"),
});
export type PurchaseOrderInput = z.input<typeof purchaseOrderSchema>;
export type PurchaseOrderData = z.output<typeof purchaseOrderSchema>;

/**
 * Unit cost a new purchase line starts at: the supplier's own price when it is in the order's
 * currency, else the weighted average cost for a euro order (the average is in euros), else none.
 * One rule for the editor, the suggestions and the automatic replenishment.
 */
export function defaultUnitCost(input: {
  orderCurrency: string;
  supplierCostCents: number | null | undefined;
  supplierCurrency: string | null | undefined;
  averageCostCents: number | null | undefined;
}): number | null {
  if (input.supplierCostCents != null && input.supplierCurrency === input.orderCurrency) return input.supplierCostCents;
  if (input.orderCurrency === "EUR" && input.averageCostCents) return input.averageCostCents;
  return null;
}

/**
 * How a purchase line names the article for the supplier (calls of 26 and 27/10: suppliers read
 * English, and only English): the supplier's own designation of the variant; else the product's
 * designation for suppliers, alone (the options are French); else the catalog title followed by
 * the options, so a missing designation shows.
 */
export function purchaseLineLabel(line: {
  title: string;
  options: { value: string }[];
  productSupplierLabel?: string | null;
  supplierLabel?: string | null;
}): string {
  if (line.supplierLabel) return line.supplierLabel;
  if (line.productSupplierLabel) return line.productSupplierLabel;
  const options = line.options.map((o) => o.value).join(" / ");
  return [line.title, options].filter(Boolean).join(" — ");
}

export const receiptSchema = z.object({
  /** Day the goods arrived: never in the future (like a stock count). */
  receivedAt: isoDate.refine((v) => v <= parisToday(), "errors.field.dateFuture"),
  /** EUR per unit of the order currency at receipt; freezes the cost booked in stock. */
  exchangeRate: z.coerce.number().positive("errors.field.positive").max(10_000),
  lines: z
    .array(z.object({ lineId: z.uuid(), quantity, serials: serialsSchema }))
    .min(1, "errors.field.required")
    .refine(...uniqueBy("lineId")),
});
export type ReceiptInput = z.input<typeof receiptSchema>;
export type ReceiptData = z.output<typeof receiptSchema>;

export const SUPPLIER_PAYMENT_KIND_VALUES = ["deposit", "balance", "transport", "other"] as const;
export const supplierPaymentSchema = z.object({
  kind: z.enum(SUPPLIER_PAYMENT_KIND_VALUES),
  /** The company account the money left from (Réglages › Achats), to trace it in the books. */
  bankAccount: optionalText(80),
  amountCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(1, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  paidAt: isoDate,
  reference: optionalText(200),
});
export type SupplierPaymentInput = z.input<typeof supplierPaymentSchema>;

/** The company's payment accounts, offered when a supplier payment is entered (the first by default). */
export const paymentAccountsSchema = z.object({
  accounts: z
    .array(z.string().trim().min(1, "errors.field.required").max(80, "errors.field.tooLong"))
    .max(20)
    .refine((a) => new Set(a.map((x) => x.toLowerCase())).size === a.length, "errors.field.duplicateLine"),
});
export type PaymentAccountsInput = z.input<typeof paymentAccountsSchema>;

export const PURCHASE_SORT_KEYS = ["date", "number", "supplier", "totalCents", "expectedAt", "status"] as const;

export const purchaseQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum([...PO_STATUS_VALUES, "open", "archived"]).optional(),
  /** "1": only the replenishment drafts. */
  auto: z.literal("1").optional(),
  supplierId: z.uuid().optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  /** Sortable columns of the list; the newest first by default. */
  sort: z.enum(PURCHASE_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});

/** Suppliers of one variant: replaces the whole list. */
export const variantSuppliersSchema = z.object({
  items: z
    .array(
      z.object({
        supplierId: z.uuid("errors.field.required"),
        supplierSku: optionalText(64),
        /** The supplier's name for the variant on its purchase orders; wins over the product's designation. */
        supplierLabel: optionalText(200),
        costCents: z.coerce
          .number()
          .int("errors.field.number")
          .min(0, "errors.field.positive")
          .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
          .nullable()
          .optional(),
        currency: z.string().length(3).toUpperCase().default("EUR"),
        leadTimeDays: z.coerce.number().int().min(0).max(365).nullable().optional(),
        minQuantity: z.coerce.number().int().min(1, "errors.field.positive").max(1_000_000).default(1),
      }),
    )
    .max(20)
    .refine((items) => new Set(items.map((i) => i.supplierId)).size === items.length, "errors.field.duplicateSupplier"),
});
export type VariantSuppliersInput = z.input<typeof variantSuppliersSchema>;

// ── After-sales ──────────────────────────────────────────────────────────────

export const SAV_STATUS_VALUES = ["open", "awaiting_return", "received", "resolved", "closed"] as const;
export const SAV_REASON_VALUES = [
  "defect",
  "damaged",
  "size_exchange",
  "product_exchange",
  "wrong_item",
  "withdrawal",
  "cancellation",
  "other",
] as const;
export type SavReason = (typeof SAV_REASON_VALUES)[number];
export const SAV_RESOLUTION_VALUES = ["refund", "replacement", "repair", "none"] as const;
export const RETURN_CONDITION_VALUES = ["restock", "quarantine", "scrap"] as const;
/** What the customer asks for: a product problem solved, another size or model, or their money back. */
export const SAV_KIND_VALUES = ["sav", "exchange", "return"] as const;
export type SavKind = (typeof SAV_KIND_VALUES)[number];

/** Kind a case gets when none is chosen: a size exchange is an exchange, a withdrawal a return. */
/** A reason added in Settings › Company › After-sales, next to the built-in ones. */
export const savCustomReasonSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^c-[a-z0-9-]{1,40}$/, "errors.field.required"),
  label: z.string().trim().min(1, "errors.field.required").max(80, "errors.field.tooLong"),
  kind: z.enum(SAV_KIND_VALUES),
});
export type SavCustomReason = z.output<typeof savCustomReasonSchema>;
/** Where customers send their returns (a post office for instance), printed as the return label's addressee. */
export const savReturnAddressSchema = z.object({
  name: z.string().trim().min(1, "errors.field.required").max(120, "errors.field.tooLong"),
  address: addressSchema,
});
export type SavReturnAddress = z.output<typeof savReturnAddressSchema>;

export const savReasonsSchema = z.object({
  /** Left out, the return address stays as it is; null goes back to the company's address. */
  returnAddress: savReturnAddressSchema.nullable().optional(),
  /** Left out, unchanged: e-mail the return label to the customer on an exchange. */
  exchangeEmailLabel: z.boolean().optional(),
  /** Left out, unchanged: copy e-mailed return labels to the company's address. */
  labelCopyToCompany: z.boolean().optional(),
  reasons: z
    .array(savCustomReasonSchema)
    .max(50)
    .refine((list) => new Set(list.map((r) => r.code)).size === list.length, "errors.field.duplicate"),
});
export const isBuiltInSavReason = (reason: string): reason is SavReason =>
  (SAV_REASON_VALUES as readonly string[]).includes(reason);

/**
 * The type of request each built-in reason belongs to: a product problem (SAV), an exchange, or a
 * return (the customer changes their mind). « Autre » fits any type.
 */
export const SAV_REASON_KINDS: Record<SavReason, SavKind | null> = {
  defect: "sav",
  damaged: "sav",
  size_exchange: "exchange",
  product_exchange: "exchange",
  wrong_item: "exchange",
  withdrawal: "return",
  cancellation: "return",
  other: null,
};

export function savKindOf(reason: string, custom: SavCustomReason[] = []): SavKind {
  if (!isBuiltInSavReason(reason)) return custom.find((r) => r.code === reason)?.kind ?? "sav";
  return SAV_REASON_KINDS[reason] ?? "sav";
}

/** Reasons offered once the type of request is chosen: its own, the custom ones of that type, and « Autre ». */
export function savReasonsFor(kind: SavKind, custom: SavCustomReason[] = []): string[] {
  return [
    ...SAV_REASON_VALUES.filter((r) => SAV_REASON_KINDS[r] === kind),
    ...custom.filter((r) => r.kind === kind).map((r) => r.code),
    "other",
  ];
}

/**
 * Condition proposed when an article comes back: a product problem (SAV: defect, damage in transit)
 * is not put back for sale by default; an exchange or a change of mind usually is.
 */
export const defaultReturnCondition = (kind: SavKind): (typeof RETURN_CONDITION_VALUES)[number] =>
  kind === "sav" ? "quarantine" : "restock";

/** Weight counted for an article whose own weight is unknown, and for a parcel with nothing known. */
export const DEFAULT_PARCEL_WEIGHT_GRAMS = 1000;
export const MAX_PARCEL_WEIGHT_GRAMS = 30_000;

/** Parcel weight from its articles (unit weight × quantity), the default standing in for unknown weights. */
export function parcelWeightOf(
  lines: { unitWeightGrams: number | null; quantity: number }[],
  defaultGrams = DEFAULT_PARCEL_WEIGHT_GRAMS,
): number {
  const total = lines.reduce((sum, l) => sum + (l.unitWeightGrams ?? defaultGrams) * Math.max(0, l.quantity || 0), 0);
  return total > 0 ? Math.min(MAX_PARCEL_WEIGHT_GRAMS, Math.round(total)) : defaultGrams;
}

const parcelGrams = z.coerce.number().int("errors.field.number").min(1, "errors.field.positive").max(MAX_PARCEL_WEIGHT_GRAMS);

/**
 * Average weights set in the settings: one per product type (the free text of the catalogue) and a
 * default for every other article. An article with its own weight never uses them.
 */
/**
 * Content categories of La Poste Pro Expédition (their referential, code → French label), the ones
 * that fit protective gear and accessories. Any other code of the referential can be typed by hand.
 */
export const LAPOSTE_CONTENT_CATEGORIES = [
  ["40110", "Tissus, vêtements neufs"],
  ["40125", "Accessoires vestimentaires, de mode"],
  ["50120", "Autres appareils et matériels"],
  ["50150", "Pièces de rechange et accessoires (autres)"],
  ["50350", "Articles de sport (hors vêtement)"],
  ["50390", "Matériel de labo, optique, de mesure"],
  ["70100", "Bagages, valises, malles"],
] as const;
export const DEFAULT_LAPOSTE_CATEGORY = "50120";

/** Parcel format sent to La Poste Pro Expédition for the articles of a product type: dimensions and content category. */
export const parcelFormatSchema = z.object({
  lengthCm: parcelCm,
  widthCm: parcelCm,
  heightCm: parcelCm,
  categoryCode: z.string().regex(/^\d{5}$/, "errors.field.number"),
});
export type ParcelFormat = z.output<typeof parcelFormatSchema>;
export const DEFAULT_PARCEL_FORMAT: ParcelFormat = {
  lengthCm: 40,
  widthCm: 30,
  heightCm: 15,
  categoryCode: DEFAULT_LAPOSTE_CATEGORY,
};

const productType = z.string().min(1, "errors.field.required").max(200, "errors.field.tooLong");

/** A box the warehouse keeps in stock (Settings › Parcels): picked at shipment, its dimensions go to La Poste. */
export const parcelBoxSchema = z
  .object({
    id: z.string().trim().min(1).max(40),
    name: z.string().trim().min(1, "errors.field.required").max(60, "errors.field.tooLong"),
    /** A flat envelope (« pli ») rather than a box: no height. */
    envelope: z.boolean().default(false),
    lengthCm: parcelCm,
    widthCm: parcelCm,
    heightCm,
    /** Weight of the empty box, added to the articles' weight. */
    emptyGrams: z.coerce.number().int().min(0, "errors.field.positive").max(10_000).default(0),
    categoryCode: z.string().regex(/^\d{5}$/, "errors.field.number"),
  })
  .refine(heightUnlessEnvelope, { path: ["heightCm"], message: "errors.field.required" });
export type ParcelBox = z.output<typeof parcelBoxSchema>;
export type ParcelBoxInput = z.input<typeof parcelBoxSchema>;

/**
 * Colissimo label formats the ERP offers (SLS `outputPrintingType`): a 10×15 cm label for a thermal
 * label printer, or the same label on an A4 page for an ordinary printer.
 */
export const LABEL_FORMATS = ["PDF_10x15_300dpi", "PDF_A4_300dpi"] as const;
export type LabelFormat = (typeof LABEL_FORMATS)[number];
export const DEFAULT_LABEL_FORMAT: LabelFormat = "PDF_10x15_300dpi";

/**
 * Chronopost label formats the ERP offers (ShippingServiceWS `mode`), PDF only since labels are stored
 * and printed as PDF: `PDF` an A4 page with the proof of deposit, `SPD` the same without the proof,
 * `THE` a 10×15 cm label for a thermal printer, without the proof. The ZPL modes (Z2D, ZPL300) are not offered.
 */
export const CHRONOPOST_LABEL_FORMATS = ["PDF", "SPD", "THE"] as const;
export type ChronopostLabelFormat = (typeof CHRONOPOST_LABEL_FORMATS)[number];
/** What the ERP sent before the choice existed. */
export const DEFAULT_CHRONOPOST_LABEL_FORMAT: ChronopostLabelFormat = "PDF";

export const parcelWeightsSchema = z.object({
  defaultGrams: parcelGrams,
  /** Colissimo label format; settings saved before it existed print 10×15. */
  labelFormat: z.enum(LABEL_FORMATS).optional(),
  /** Chronopost label format; settings saved before it existed print an A4 page (`PDF`). */
  chronopostLabelFormat: z.enum(CHRONOPOST_LABEL_FORMATS).optional(),
  byType: z.record(productType, parcelGrams).refine((v) => Object.keys(v).length <= 500, "errors.field.tooLong"),
  /** Boxes in stock, in the order of the shipment form's list. */
  boxes: z.array(parcelBoxSchema).max(30).optional(),
  /**
   * Format of the La Poste export when no box was chosen; settings saved before this existed have none
   * and use the built-in one. `byType` (a format per family) is no longer used: kept so older settings read.
   */
  formats: z
    .object({
      default: parcelFormatSchema.default(DEFAULT_PARCEL_FORMAT),
      byType: z.record(productType, parcelFormatSchema).default({}),
    })
    .optional(),
});
export type ParcelWeightsInput = z.input<typeof parcelWeightsSchema>;
export type ParcelWeightsData = z.output<typeof parcelWeightsSchema>;

/** Unit weight counted for an article: its own (0 g means unknown), else the average of its product type, else the default. */
/** Parcel format of the La Poste file when no box was chosen (also when the settings predate formats). */
export function parcelFormatOf(weights: ParcelWeightsData): ParcelFormat {
  return weights.formats?.default ?? DEFAULT_PARCEL_FORMAT;
}

/** Colissimo label format chosen in the settings (10×15 when the settings predate the choice). */
export function labelFormatOf(weights: Pick<ParcelWeightsData, "labelFormat">): LabelFormat {
  return weights.labelFormat ?? DEFAULT_LABEL_FORMAT;
}

/** Chronopost label format chosen in the settings (A4 `PDF` when the settings predate the choice). */
export function chronopostLabelFormatOf(weights: Pick<ParcelWeightsData, "chronopostLabelFormat">): ChronopostLabelFormat {
  return weights.chronopostLabelFormat ?? DEFAULT_CHRONOPOST_LABEL_FORMAT;
}

export function unitWeightOf(
  article: { weightGrams: number | null; productType: string | null },
  weights: ParcelWeightsData,
): number {
  return article.weightGrams || (article.productType ? weights.byType[article.productType] : undefined) || weights.defaultGrams;
}

export const savTicketSchema = z.object({
  orderId: z.uuid().nullable().optional(),
  customerId: z.uuid().nullable().optional(),
  /** The person concerned when it is not the order's customer (a grouped order): their name on the case and its parcels. */
  contactName: optionalText(120),
  /** Left empty, derived from the reason (`savKindOf`). */
  kind: z.enum(SAV_KIND_VALUES).nullable().optional(),
  /** A built-in reason, or the code of one added in the settings. */
  reason: z.string().trim().min(1, "errors.field.required").max(60),
  subject: z.string().trim().min(1, "errors.field.required").max(200, "errors.field.tooLong"),
  description: optionalText(10_000),
  lines: z
    .array(
      z.object({
        orderLineId: z.uuid().nullable().optional(),
        variantId: z.uuid().nullable().optional(),
        label: z.string().trim().min(1, "errors.field.required").max(500),
        sku: optionalText(64),
        quantity: z.coerce.number().int().min(1, "errors.field.positive").max(10_000),
      }),
    )
    .max(100)
    .default([]),
});
export type SavTicketInput = z.input<typeof savTicketSchema>;
export type SavTicketData = z.output<typeof savTicketSchema>;

export const savReceiptSchema = z.object({
  lines: z
    .array(z.object({ lineId: z.uuid(), quantity, condition: z.enum(RETURN_CONDITION_VALUES), serials: serialsSchema }))
    .min(1, "errors.field.required"),
});
export type SavReceiptInput = z.input<typeof savReceiptSchema>;

/** Left empty, the weight is estimated from the articles in the parcel. */
const parcelWeight = z.coerce
  .number()
  .int("errors.field.number")
  .min(1, "errors.field.positive")
  .max(MAX_PARCEL_WEIGHT_GRAMS)
  .nullable()
  .optional();

const savParcel = {
  weightGrams: parcelWeight,
  generateLabel: z.boolean().default(false),
  trackingNumber: z.string().trim().max(64).nullable().optional(),
};

export const savReturnLabelSchema = z.object({
  ...savParcel,
  /** Box and weight of the return parcel (it may differ from the order's). */
  parcels: z.array(shipmentParcelSchema).max(10).optional(),
  /** Addressee of this return, when not the usual return address. */
  returnTo: savReturnAddressSchema.nullable().optional(),
  /** E-mails the label to the customer once it is made. */
  emailCustomer: z.boolean().default(false),
});
export type SavReturnLabelInput = z.input<typeof savReturnLabelSchema>;

/** Items sent to the customer; `variantId` sends another article (other size, other model) instead of the case line's. */
const replacementLines = z
  .array(z.object({ lineId: z.uuid(), quantity, variantId: z.uuid().nullable().optional(), serials: serialsSchema }))
  .min(1, "errors.field.required");

/** Boxes of the parcel sent to the customer (it may differ from the order's: a cummerbund alone travels small). */
const outgoingParcels = z.array(shipmentParcelSchema).max(10).optional();

export const savReplacementSchema = z.object({ ...savParcel, parcels: outgoingParcels, lines: replacementLines });
export type SavReplacementInput = z.input<typeof savReplacementSchema>;

/**
 * Exchange in one step: the new article leaves with a return label in the box, and the case waits
 * for the old one. `generateLabel` applies to both parcels.
 */
export const savExchangeSchema = z.object({
  ...savParcel,
  parcels: outgoingParcels,
  lines: replacementLines,
  returnParcels: z.array(shipmentParcelSchema).max(10).optional(),
  returnTo: savReturnAddressSchema.nullable().optional(),
  returnWeightGrams: parcelWeight,
  returnTrackingNumber: z.string().trim().max(64).nullable().optional(),
  /** E-mail the return label to the customer; left out, the setting of Settings › After-sales decides. */
  emailCustomer: z.boolean().optional(),
});
export type SavExchangeInput = z.input<typeof savExchangeSchema>;

export const savNoteSchema = z.object({ body: z.string().trim().min(1, "errors.field.required").max(10_000) });

export const SAV_ATTACHMENT_MAX_BYTES = ATTACHMENT_MAX_BYTES;
/** Photos and documents from the customer, sent base64-encoded (at most 10 MB once decoded). */
export const savAttachmentSchema = attachmentSchema;
export type SavAttachmentInput = z.input<typeof savAttachmentSchema>;

export const SAV_SORT_KEYS = ["createdAt", "number", "customer", "status", "updatedAt"] as const;

export const savQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum([...SAV_STATUS_VALUES, "active"]).optional(),
  kind: z.enum(SAV_KIND_VALUES).optional(),
  customerId: z.uuid().optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  /** Sortable columns of the list; the newest first by default. */
  sort: z.enum(SAV_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});

// ── Purchase documents (supplier invoices, receipts, contracts) ───────────────

/**
 * Files kept for the accountant: supplier invoices, credit notes and receipts become "supplier bills"
 * at Inqom, the rest plain documents. A supplier credit note linked to an order lowers what is still
 * owed on it.
 */
export const PURCHASE_DOCUMENT_KINDS = ["supplier_invoice", "supplier_credit", "receipt", "contract", "other"] as const;
export type PurchaseDocumentKind = (typeof PURCHASE_DOCUMENT_KINDS)[number];
export const PURCHASE_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;

const purchaseDocumentFields = {
  kind: z.enum(PURCHASE_DOCUMENT_KINDS),
  amountCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
    .nullable()
    .optional(),
  documentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "errors.field.date")
    .nullable()
    .optional(),
  supplierId: z.uuid().nullable().optional(),
  purchaseOrderId: z.uuid().nullable().optional(),
  note: optionalText(1000),
};
/** A credit note without its amount could not be deducted from the order. */
const creditAmount = (doc: { kind: PurchaseDocumentKind; amountCents?: number | null }, ctx: z.RefinementCtx) => {
  if (doc.kind === "supplier_credit" && !doc.amountCents)
    ctx.addIssue({ code: "custom", path: ["amountCents"], message: "errors.field.required" });
};
export const purchaseDocumentSchema = z.object({ ...purchaseDocumentFields, file: attachmentSchema }).superRefine(creditAmount);
export type PurchaseDocumentInput = z.input<typeof purchaseDocumentSchema>;
export type PurchaseDocumentData = z.output<typeof purchaseDocumentSchema>;
export const purchaseDocumentUpdateSchema = z.object(purchaseDocumentFields).superRefine(creditAmount);
export type PurchaseDocumentUpdateData = z.output<typeof purchaseDocumentUpdateSchema>;
export const purchaseDocumentQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  kind: z.enum(PURCHASE_DOCUMENT_KINDS).optional(),
  supplierId: z.uuid().optional(),
  purchaseOrderId: z.uuid().optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** "1": handed to the accountant, "0": not yet. */
  transmitted: z.enum(["0", "1"]).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PurchaseDocumentQuery = z.output<typeof purchaseDocumentQuerySchema>;
